import React, { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Menu, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { RenalifeWelcome } from "./RenalifeWelcome";
import { RenalifeUpsell, isRenalifeAllowedPath } from "./RenalifeUpsell";
import { SidebarContent, buildMenuItems } from "./Sidebar";
import { api, apiError } from "../lib/api";

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === "/dashboard" || location.pathname === "/dashboard/";
  const isRenalife = user?.pin_type === "renalife";
  const menuItems = buildMenuItems(user);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const saveGuestData = async () => {
    try {
      await api.post("/pin/save-data");
      toast.success("¡Tus datos quedaron guardados! Cuando uses otro PIN con tu mismo WhatsApp, se rellenarán solos.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const finishGuestCampaign = async () => {
    const ok = window.confirm(
      "Al finalizar se borrarán los archivos de tu campaña (textos, imágenes y videos).\n\n" +
      "Tu página de captura seguirá activa y tus prospectos seguirán llegando a tu WhatsApp.\n\n" +
      "Si quieres conservar tus datos para una próxima campaña, presiona primero 'Guardar datos'.\n\n¿Deseas finalizar?"
    );
    if (!ok) return;
    try {
      await api.post("/pin/finalize");
      toast.success("Campaña finalizada. ¡Gracias por usar MULTICONTACTOS!");
      await logout();
      navigate("/login");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const sidebarProps = {
    user, menuItems,
    onNavigate: () => setOpen(false),
    onLogout: doLogout,
    onSaveData: saveGuestData,
    onFinalize: finishGuestCampaign,
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/10 bg-[#0a0a0a] fixed inset-y-0">
        <SidebarContent {...sidebarProps} />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-64 bg-[#0a0a0a] border-r border-white/10">
            <SidebarContent {...sidebarProps} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64">
        {/* Mobile topbar */}
        <header className="lg:hidden sticky top-0 z-40 glass border-b border-white/10 flex items-center justify-between px-4 h-14">
          <div className="text-base font-display font-black">
            MULTI<span className="text-purple-500">CONTACTOS</span>
          </div>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </header>
        <main className="p-5 sm:p-8 max-w-6xl mx-auto">
          {isHome && isRenalife && <RenalifeWelcome />}
          {!isHome && (
            <button
              data-testid="back-button"
              onClick={() => navigate(-1)}
              className="group mb-5 inline-flex items-center gap-2 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
            >
              <span className="w-8 h-8 rounded-full border border-white/10 bg-[#121212] flex items-center justify-center group-hover:border-purple-500/50 group-hover:bg-purple-600/15 transition-colors">
                <ArrowLeft size={16} />
              </span>
              Regresar
            </button>
          )}
          {isRenalife && !isRenalifeAllowedPath(location.pathname) ? <RenalifeUpsell /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
